import { useEffect, useState, useRef } from "react";
import * as S from "./styles";
import { useApi } from "../../contexts/api";
import { TabelaLib } from "../TabelaLib";
import Close from "@mui/icons-material/Close";
import { useTheme } from "@emotion/react";
import logoLoja from "../../assets/images/logoLoja.jpg";
import Carregando from "../../pages/carregando";
import dayjs from "dayjs";
import { formatarInscricaoFederal } from "../../utils/formatarInscricaoFederal";
import { IndicadorStatus } from "../IndicadorStatus";
import podeFecharAoClicarFora from "../../utils/fecharAoClicarFora";

const STATUS_EMPRESA_VALIDOS = ["ATIVO", "INATIVO", "TODOS"];
const IDENTIFICADOR_TABELA = "alterar_empresa";

const normalizarFiltroStatus = (filtro = {}) => {
  const colunasValores = { ...(filtro.colunasValores ?? {}) };
  const statusRecebido =
    colunasValores.status == null || colunasValores.status === "" ? "ATIVO" : colunasValores.status;
  const statusNormalizado = String(statusRecebido).toUpperCase();

  colunasValores.status = STATUS_EMPRESA_VALIDOS.includes(statusNormalizado)
    ? statusNormalizado
    : "ATIVO";

  delete colunasValores.ativo;

  const filtroNormalizado = { ...filtro, colunasValores };

  if (filtroNormalizado.valoresSelect) {
    filtroNormalizado.valoresSelect = {
      ...filtroNormalizado.valoresSelect,
      status: colunasValores.status,
    };
    delete filtroNormalizado.valoresSelect.ativo;
  }

  delete filtroNormalizado.ativo;

  return filtroNormalizado;
};

export default function SelecionarEmpresaPopup({
  selecionarEmpresa = () => {},
  fecharTabela = () => {},
}) {
  const { post } = useApi();
  const theme = useTheme();
  const [dadosIniciaisBuscados, setDadosIniciaisBuscados] = useState(false);
  const [dadosTabela, setDadosTabela] = useState([]);
  const [dadosTabelaConfig, setDadosTabelaConfig] = useState({});
  const filtroRef = useRef({});
  const timeoutRef = useRef(null);
  const sizeRef = useRef(10);

  const buscarEmpresas = (pagina = 0, quantidade = 10, filtroNovo = null) => {
    if (filtroNovo !== null) {
      filtroRef.current = filtroNovo;
    }

    sizeRef.current = quantidade;

    const corpoRequisicao = normalizarFiltroStatus(JSON.parse(JSON.stringify(filtroRef.current)));

    post(
      `/configuracoes-lojas/empresa/buscar-empresas?page=${pagina}&size=${quantidade}`,
      corpoRequisicao,
    )
      .then((dados) => {
        setDadosIniciaisBuscados(true);
        renderizarItensTabela(dados.data);
      })
      .catch((erro) => {
        setDadosIniciaisBuscados(true);
      });
  };

  const enumColunas = (coluna) => {
    const colunas = {
      nomeFantasia: "NOME_FANTASIA",
      tipo: "TIPO",
      grupo: "GRUPO",
      integrador: "INTEGRADOR",
      cidade: "CIDADE",
      uf: "UF",
      plano: "PLANO",
      assinatura: "ASSINATURA",
      dataCadastro: "DATA_CADASTRO",
      vencimento: "VENCIMENTO",
      inscricaoFederal: "INSCRICAO_FEDERAL",
    };
    return colunas[coluna];
  };

  const filtrarDados = (dados) => {
    const quantidade = dados.linhasPorPagina ?? 10;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      const busca = {
        ...dados.conteudoBody,
        ordenarEmpresas:
          dados?.ordenacao?.coluna === "" ? null : enumColunas(dados?.ordenacao?.coluna),
        direcionamento: dados?.ordenacao?.direcao === "" ? null : dados?.ordenacao?.direcao,
      };

      if (dados?.conteudoBody?.colunasValores?.inscricaoFederal) {
        busca.colunasValores.inscricaoFederal = dados.conteudoBody.colunasValores.inscricaoFederal
          .replace(/[^a-zA-Z0-9]/g, "")
          .toUpperCase();
      }

      if (dados?.conteudoBody?.palavraChave) {
        const value = dados.conteudoBody.palavraChave;
        const onlyDigits = value.replace(/\D/g, "");
        const isOnlyNumbersOrSymbols = /^[\d.,\s]+$/.test(value);
        const finalValue = isOnlyNumbersOrSymbols ? onlyDigits : value;
        busca.palavraChave = finalValue;
      }

      sizeRef.current = dados?.conteudoBody.linhasPorPagina;

      buscarEmpresas(0, quantidade, busca);
    }, 400);
  };

  function renderizarItensTabela(dados) {
    const dadosItems = dados?.content;
    const paginacao = dados?.pageable;

    if (dadosItems && paginacao) {
      const novosDadosTabela = {
        paginas: dados?.totalPages,
        pagina: paginacao?.pageNumber,
        totalElementos: dados?.totalElements,
        mudarPagina: (pagina, dados) => {
          const quantidade = Number(dados?.linhasPorPagina ?? 10);
          buscarEmpresas(
            pagina,
            quantidade,
            dados?.conteudoBody
              ? {
                  ...dados.conteudoBody,
                  ordenarEmpresas:
                    dados?.ordenacao?.coluna === "" ? null : enumColunas(dados?.ordenacao?.coluna),
                  direcionamento:
                    dados?.ordenacao?.direcao === "" ? null : dados?.ordenacao?.direcao,
                }
              : null,
          );
        },
        filtragemDados: () => {},
        valoresColuna: filtrarDados,
        eventoCliqueLinha: (item) => {
          selecionarEmpresa(item.id, item.nome);
        },
        colunas: [
          {
            key: "status",
            titulo: "Status",
            esconder: false,
            valorPadrao: "ATIVO",
            manterTodosNoFiltro: true,
          },
          {
            key: "imagem",
            titulo: "Logo",
            ordenacao: false,
            campoDeTexto: false,
          },
          { key: "nomeFantasia", titulo: "Nome fantasia", ordenacao: true },
          { key: "tipo", titulo: "Tipo", ordenacao: true, campoDeTexto: true },
          { key: "grupo", titulo: "Grupo", ordenacao: true },
          { key: "integrador", titulo: "Integrador", ordenacao: true },
          { key: "inscricaoFederal", titulo: "Ins. Federal", ordenacao: false },
          { key: "cidade", titulo: "Cidade", ordenacao: true },
          { key: "uf", titulo: "UF", ordenacao: true },
          { key: "plano", titulo: "Plano", ordenacao: true },
          { key: "assinatura", titulo: "Assinatura", ordenacao: true },
          { key: "dataCadastro", titulo: "Data cadastro", ordenacao: true },
        ],
        gridColuna: `minmax(150px, 1fr) minmax(120px, 1fr) minmax(260px, 1fr) minmax(170px, 1fr) minmax(200px, 1fr) minmax(170px, 1fr) minmax(250px, 1fr) minmax(250px, 1fr) minmax(100px, 1fr) minmax(130px, 1fr) minmax(180px, 1fr) minmax(180px, 1fr)!important`,
        nodes: [],
        nodesTabelaSecundaria: [],
      };

      for (let i = 0; i < dadosItems.length; i++) {
        const item = dadosItems[i];
        const statusAtivo = item.status === "ATIVO";

        novosDadosTabela.nodes.push({
          status: statusAtivo ? (
            <IndicadorStatus cor="verde" texto="Ativo" />
          ) : (
            <IndicadorStatus cor="vermelho" texto="Inativo" />
          ),
          imagem: (
            <S.CardLojaImage
              src={
                item.logo === null || item.logo === ""
                  ? logoLoja
                  : `data:image/jpg;base64,${item.logo}`
              }
            />
          ),
          nomeFantasia: item.nomeFantasia,
          tipo: item.tipoEmpresa,
          grupo: item.grupo,
          integrador: item.integrador,
          inscricaoFederal: formatarInscricaoFederal(item.inscricaoFederal),
          cidade: item.cidade,
          uf: item.uf,
          plano: item.tipoContrato,
          assinatura: item.recorrenciaContrato,
          dataCadastro: dayjs(item.dataCadastro).format("DD/MM/YYYY"),
          parametro: { id: item.id, nome: item.nomeFantasia },
        });
      }

      setDadosTabelaConfig({
        containerFiltroGeral: true,
        filtroGeral: true,
        ordenacao: true,
        filtroColuna: true,
        filtroColunaVisivel: false,
        esconderColuna: false,
        accordionTabela: false,
        pesquisarEmpresas: true,
      });

      setDadosTabela(novosDadosTabela);
    } else {
      setDadosTabela([]);
    }
  }

  const carregarFiltroGeralStorage = () => {
    try {
      const url = window.location.pathname;
      const chave = `tabela_filtro_geral_${url}_${IDENTIFICADOR_TABELA}`.replace(
        /[^a-zA-Z0-9_]/g,
        "_",
      );
      const configuracaoSalva = localStorage.getItem(chave);

      if (configuracaoSalva) {
        const config = JSON.parse(configuracaoSalva);
        const trintaDias = 30 * 24 * 60 * 60 * 1000;
        if (config.timestamp && Date.now() - config.timestamp < trintaDias) {
          return config.valorFiltroGeral || "";
        }
      }

      return "";
    } catch (error) {
      return "";
    }
  };

  const carregarFiltroColunasStorage = () => {
    try {
      const url = window.location.pathname;
      const chave = `tabela_filtro_colunas_${url}_${IDENTIFICADOR_TABELA}`.replace(
        /[^a-zA-Z0-9_]/g,
        "_",
      );
      const configuracaoSalva = localStorage.getItem(chave);

      if (configuracaoSalva) {
        const config = JSON.parse(configuracaoSalva);
        const trintaDias = 30 * 24 * 60 * 60 * 1000;
        if (config.timestamp && Date.now() - config.timestamp < trintaDias) {
          const filtrosColunas = { ...(config.filtrosColunas || {}) };
          delete filtrosColunas.ativo;
          return filtrosColunas;
        }
      }

      return {};
    } catch (error) {
      return {};
    }
  };

  useEffect(() => {
    const filtroGeralSalvo = carregarFiltroGeralStorage();
    const filtrosColunasSalvos = carregarFiltroColunasStorage();

    const filtroInicial = {
      colunasValores: {
        status: "ATIVO",
      },
    };

    if (filtroGeralSalvo && filtroGeralSalvo.trim() !== "") {
      const value = filtroGeralSalvo;
      const onlyDigits = value.replace(/\D/g, "");
      const isOnlyNumbersOrSymbols = /^[\d.,\s]+$/.test(value);
      const finalValue = isOnlyNumbersOrSymbols ? onlyDigits : value;
      filtroInicial.palavraChave = finalValue;
    }

    if (filtrosColunasSalvos && Object.keys(filtrosColunasSalvos).length > 0) {
      filtroInicial.colunasValores = {
        ...filtroInicial.colunasValores,
        ...filtrosColunasSalvos,
      };

      if (filtrosColunasSalvos.inscricaoFederal) {
        filtroInicial.colunasValores.inscricaoFederal = filtrosColunasSalvos.inscricaoFederal
          .replace(/[^a-zA-Z0-9]/g, "")
          .toUpperCase();
      }
    }

    filtroRef.current = filtroInicial;
    buscarEmpresas(0, sizeRef.current, filtroInicial);
  }, []);

  return (
    <S.BottomSheetCustomizada
      onDismiss={() => {
        if (podeFecharAoClicarFora()) fecharTabela();
      }}
      open
      snapPoints={({ maxHeight }) => [maxHeight * 0.8]}
      blocking={false}
    >
      <S.ContainerBottomSheet>
        <S.TituloModal>
          <h3>Alterar empresa</h3>
          <S.BotaoFechar onClick={fecharTabela}>
            <Close
              style={{
                fontSize: 32,
                color: theme.cores.botaoModal,
                cursor: "pointer",
              }}
            />
          </S.BotaoFechar>
        </S.TituloModal>

        <S.Tabela>
          {!dadosIniciaisBuscados ? (
            <Carregando />
          ) : (
            dadosTabela.nodes && (
              <TabelaLib
                dadosTabela={dadosTabela}
                variante={dadosTabelaConfig}
                identificadorTabela={IDENTIFICADOR_TABELA}
                focoAutomaticoFiltro
                requisicaoInicialJaFeita
              />
            )
          )}
        </S.Tabela>
      </S.ContainerBottomSheet>
    </S.BottomSheetCustomizada>
  );
}
